import type { TFunction } from "i18next";

import { NotificationKind, OnboardingReviewArea } from "~/gql/graphqlBase.generated";
import { type NotificationFragment } from "~/gql/queries/notification.generated";
import activeLocaleTag from "~/helpers/activeLocale";
import { formatE164Phone } from "~/helpers/fieldValidators";
import { buildReportRunPath } from "~reports/executions/helpers/reportRunResult";

const FALLBACK_ICON = "notifications";
const FALLBACK_TRANSLATION_KEY = "unknown";
const ORDER_ENTITY_TYPE = "order";
const CUSTOMER_ENTITY_TYPE = "customer";
const TENANT_ENTITY_TYPE = "tenant";
const REPORT_EXECUTION_ENTITY_TYPE = "reportexecution";
const LEARNING_CONCEPT_ENTITY_TYPE = "learningconcept";
const INBOUND_DOCUMENT_ENTITY_TYPE = "inbounddocument";
const SENDER_FIELD = "sender";
const CUSTOMER_NAME_FIELD = "customerName";
const PHONE_NUMBER_FIELD = "phoneNumber";
const REVIEW_AREAS_FIELD = "reviewAreas";
const SCHEDULED_TRANSFER_ENTITY_TYPE = "scheduledinventorytransfer";
const FROM_SKU_FIELD = "fromSku";
const TO_SKU_FIELD = "toSku";
const LOCATION_NAME_FIELD = "locationName";
const MOVED_QUANTITY_FIELD = "movedQuantity";
const HELD_BACK_QUANTITY_FIELD = "heldBackQuantity";
const SHORTFALL_QUANTITY_FIELD = "shortfallQuantity";
const OUTCOME_REASON_FIELD = "outcomeReason";
const NOTHING_AVAILABLE_REASON = "NOTHING_AVAILABLE";
const KNOWN_OUTCOME_REASONS: readonly string[] = [
	NOTHING_AVAILABLE_REASON,
	"SOURCE_KEY_MISSING",
	"DESTINATION_ITEM_NOT_ACTIVE",
	"HARD_GOOD_KEY"
];
const MAX_QUANTITY_FRACTION_DIGITS = 6;
const NOTIFICATIONS_ROUTE = "/notifications";

export interface NotificationRouteOptions {
	isLearningPathEnabled?: boolean;
	isScheduledTransfersEnabled?: boolean;
}

const KIND_ICONS: Partial<Record<NotificationKind, string>> = {
	[NotificationKind.CustomerDetailsChangedInPortal]: "contact_page",
	[NotificationKind.InboundDocumentReceived]: "inbox",
	[NotificationKind.LearningEscalation]: "support_agent",
	[NotificationKind.LearningNudge]: "school",
	[NotificationKind.MultiFactorDisabled]: "gpp_bad",
	[NotificationKind.MultiFactorEnrolled]: "gpp_good",
	[NotificationKind.OnboardingFileImported]: "fact_check",
	[NotificationKind.OrderAcknowledged]: "check_circle",
	[NotificationKind.OrderCancelled]: "cancel",
	[NotificationKind.OrderShipped]: "local_shipping",
	[NotificationKind.OrderSubmitted]: "receipt_long",
	[NotificationKind.PhoneVerificationCode]: "sms",
	[NotificationKind.ReportRunFailed]: "assignment_late",
	[NotificationKind.ReportRunSucceeded]: "assignment_turned_in",
	[NotificationKind.ScheduledTransferExecuted]: "swap_horiz",
	[NotificationKind.ScheduledTransferNotExecuted]: "event_busy",
	[NotificationKind.SmsEnrollmentConfirmed]: "sms"
};

const KIND_TRANSLATION_KEYS: Partial<Record<NotificationKind, string>> = {
	[NotificationKind.CustomerDetailsChangedInPortal]: "customerDetailsChangedInPortal",
	[NotificationKind.InboundDocumentReceived]: "inboundDocumentReceived",
	[NotificationKind.LearningEscalation]: "learningEscalation",
	[NotificationKind.LearningNudge]: "learningNudge",
	[NotificationKind.MultiFactorDisabled]: "multiFactorDisabled",
	[NotificationKind.MultiFactorEnrolled]: "multiFactorEnrolled",
	[NotificationKind.OnboardingFileImported]: "onboardingFileImported",
	[NotificationKind.OrderAcknowledged]: "orderAcknowledged",
	[NotificationKind.OrderCancelled]: "orderCancelled",
	[NotificationKind.OrderShipped]: "orderShipped",
	[NotificationKind.OrderSubmitted]: "orderSubmitted",
	[NotificationKind.PhoneVerificationCode]: "phoneVerificationCode",
	[NotificationKind.ReportRunFailed]: "reportRunFailed",
	[NotificationKind.ReportRunSucceeded]: "reportRunSucceeded",
	[NotificationKind.ScheduledTransferExecuted]: "scheduledTransferExecuted",
	[NotificationKind.ScheduledTransferNotExecuted]: "scheduledTransferNotExecuted",
	[NotificationKind.SmsEnrollmentConfirmed]: "smsEnrollmentConfirmed"
};

export const REVIEW_AREAS: Record<string, { route: string; labelKey: string }> = {
	Categories: { labelKey: "notifications.areas.categories", route: "/fulfill/product/categories" },
	Catalogs: { labelKey: "notifications.areas.catalogs", route: "/produce/production/items/catalogs" },
	Customers: { labelKey: "notifications.areas.customers", route: "/fulfill/customers" },
	Inventory: { labelKey: "notifications.areas.inventory", route: "/fulfill/inventory" },
	Items: { labelKey: "notifications.areas.items", route: "/fulfill/product/items" },
	Locations: { labelKey: "notifications.areas.locations", route: "/fulfill/locations" },
	Orders: { labelKey: "notifications.areas.orders", route: "/fulfill/orders" },
	Pricing: { labelKey: "notifications.areas.pricing", route: "/fulfill/sales/pricing/groups" },
	ProductionItems: {
		labelKey: "notifications.areas.productionItems",
		route: "/produce/production/items/production-items"
	},
	Recipes: { labelKey: "notifications.areas.recipes", route: "/produce/production/recipes" },
	Schedule: { labelKey: "notifications.areas.schedule", route: "/produce/production/schedule" },
	SpaceCategories: { labelKey: "notifications.areas.spaceCategories", route: "/produce/capacity/categories-and-types" },
	Terms: { labelKey: "notifications.areas.terms", route: "/fulfill/sales/terms" },
	Vendors: { labelKey: "notifications.areas.vendors", route: "/fulfill/purchasing/vendors" }
};

// tenant.onboardingUploads[].reviewAreas is the generated GraphQL enum (SCREAMING_CASE); REVIEW_AREAS is keyed by the wsapi member name a notification's reviewAreas field and ?area= link use instead
const REVIEW_AREA_CODE_BY_ENUM: Record<OnboardingReviewArea, string | null> = {
	[OnboardingReviewArea.Unknown]: null,
	[OnboardingReviewArea.Catalogs]: "Catalogs",
	[OnboardingReviewArea.Categories]: "Categories",
	[OnboardingReviewArea.Customers]: "Customers",
	[OnboardingReviewArea.Inventory]: "Inventory",
	[OnboardingReviewArea.Items]: "Items",
	[OnboardingReviewArea.Locations]: "Locations",
	[OnboardingReviewArea.Orders]: "Orders",
	[OnboardingReviewArea.Pricing]: "Pricing",
	[OnboardingReviewArea.ProductionItems]: "ProductionItems",
	[OnboardingReviewArea.Recipes]: "Recipes",
	[OnboardingReviewArea.Schedule]: "Schedule",
	[OnboardingReviewArea.SpaceCategories]: "SpaceCategories",
	[OnboardingReviewArea.Terms]: "Terms",
	[OnboardingReviewArea.Vendors]: "Vendors"
};

export const reviewAreaLabelKey = (area: OnboardingReviewArea): string | null => {
	const code = REVIEW_AREA_CODE_BY_ENUM[area];
	return code ? REVIEW_AREAS[code].labelKey : null;
};

export const notificationIcon = (kind: NotificationKind): string => KIND_ICONS[kind] ?? FALLBACK_ICON;

export const notificationTranslationKey = (kind: NotificationKind): string =>
	KIND_TRANSLATION_KEYS[kind] ?? FALLBACK_TRANSLATION_KEY;

export const notificationField = (notification: NotificationFragment, key: string): string =>
	notification.fields.find((field) => field.key === key)?.value ?? "";

export const decodeReviewAreas = (notification: NotificationFragment): string[] => {
	const raw = notificationField(notification, REVIEW_AREAS_FIELD);
	if (!raw) return [];
	return raw
		.split(",")
		.map((code) => code.trim())
		.filter((code) => Object.prototype.hasOwnProperty.call(REVIEW_AREAS, code));
};

const parseQuantity = (raw: string): number | null => {
	if (!raw.trim()) return null;
	const quantity = Number(raw);
	return Number.isFinite(quantity) ? quantity : null;
};

const formatQuantity = (quantity: number): string =>
	new Intl.NumberFormat(activeLocaleTag(), { maximumFractionDigits: MAX_QUANTITY_FRACTION_DIGITS }).format(quantity);

const buildScheduledTransferSentences = (notification: NotificationFragment, t: TFunction): string[] => {
	const isExecuted = notification.kind === NotificationKind.ScheduledTransferExecuted;
	const reason = notificationField(notification, OUTCOME_REASON_FIELD);
	const hasMeasuredQuantities = isExecuted || reason === NOTHING_AVAILABLE_REASON;
	const fromSku = notificationField(notification, FROM_SKU_FIELD) || t("notifications.scheduledTransfer.sourceItem");
	const toSku = notificationField(notification, TO_SKU_FIELD) || t("notifications.scheduledTransfer.destinationItem");
	const locationName = notificationField(notification, LOCATION_NAME_FIELD);
	const moved = parseQuantity(notificationField(notification, MOVED_QUANTITY_FIELD));
	const heldBack = parseQuantity(notificationField(notification, HELD_BACK_QUANTITY_FIELD));
	const shortfall = parseQuantity(notificationField(notification, SHORTFALL_QUANTITY_FIELD));
	const sentences: string[] = [];

	if (isExecuted) {
		if (moved === null) return sentences;
		sentences.push(t("notifications.scheduledTransfer.executed", { fromSku, quantity: formatQuantity(moved), toSku }));
	} else {
		sentences.push(t("notifications.scheduledTransfer.notExecuted", { fromSku, toSku }));
	}
	if (locationName) sentences.push(t("notifications.scheduledTransfer.sourceLocation", { locationName }));
	if (hasMeasuredQuantities && heldBack) {
		sentences.push(t("notifications.scheduledTransfer.heldBack", { quantity: formatQuantity(heldBack) }));
	}
	if (!isExecuted && hasMeasuredQuantities && shortfall) {
		sentences.push(t("notifications.scheduledTransfer.shortfall", { quantity: formatQuantity(shortfall) }));
	}
	if (!isExecuted && KNOWN_OUTCOME_REASONS.includes(reason)) {
		sentences.push(t(`notifications.scheduledTransfer.reasons.${reason}`));
	}
	return sentences;
};

export const notificationSecondaryLine = (notification: NotificationFragment, t: TFunction): string => {
	if (notification.kind === NotificationKind.SmsEnrollmentConfirmed) {
		const phoneNumber = notificationField(notification, PHONE_NUMBER_FIELD);
		return phoneNumber
			? t("notifications.smsEnrollmentConfirmedBody", { phoneNumber: formatE164Phone(phoneNumber) })
			: "";
	}
	if (notification.kind === NotificationKind.OnboardingFileImported) {
		const areas = decodeReviewAreas(notification).map((code) => t(REVIEW_AREAS[code].labelKey));
		return areas.length > 0 ? t("notifications.onboardingFileImportedBody", { areas: areas.join(", ") }) : "";
	}
	if (notification.kind === NotificationKind.InboundDocumentReceived) {
		const sender = notificationField(notification, SENDER_FIELD);
		return sender ? t("notifications.inboundDocumentReceivedBody", { sender }) : "";
	}
	if (notification.kind === NotificationKind.ReportRunSucceeded) return t("notifications.reportRunSucceededBody");
	if (notification.kind === NotificationKind.ReportRunFailed) return t("notifications.reportRunFailedBody");
	if (notification.kind === NotificationKind.LearningNudge) return t("notifications.learningNudgeBody");
	if (notification.kind === NotificationKind.LearningEscalation) return t("notifications.learningEscalationBody");
	if (
		notification.kind === NotificationKind.ScheduledTransferExecuted ||
		notification.kind === NotificationKind.ScheduledTransferNotExecuted
	) {
		return buildScheduledTransferSentences(notification, t).join(" ");
	}
	return notificationField(notification, CUSTOMER_NAME_FIELD);
};

export const isScheduledTransferNotification = (notification: NotificationFragment): boolean =>
	notification.entityType.toLowerCase() === SCHEDULED_TRANSFER_ENTITY_TYPE;

export const notificationRoute = (
	notification: NotificationFragment,
	{ isLearningPathEnabled = false, isScheduledTransfersEnabled = false }: NotificationRouteOptions = {}
): string | null => {
	const entityType = notification.entityType.toLowerCase();
	if (entityType === ORDER_ENTITY_TYPE && notification.entityId)
		return `/fulfill/orders/order/${notification.entityId}`;
	if (entityType === CUSTOMER_ENTITY_TYPE && notification.entityId)
		return `/fulfill/customers/customer/${notification.entityId}`;
	if (entityType === REPORT_EXECUTION_ENTITY_TYPE && notification.entityId)
		return buildReportRunPath(notification.entityId);
	if (entityType === INBOUND_DOCUMENT_ENTITY_TYPE && notification.entityId)
		return `/inbox/documents/${encodeURIComponent(notification.entityId)}`;
	if (isScheduledTransferNotification(notification) && notification.entityId && isScheduledTransfersEnabled)
		return `/fulfill/inventory/scheduled-transfers?id=${encodeURIComponent(notification.entityId)}`;
	if (
		isLearningPathEnabled &&
		notification.kind === NotificationKind.LearningNudge &&
		entityType === LEARNING_CONCEPT_ENTITY_TYPE &&
		notification.entityId
	)
		return `/?learn=${encodeURIComponent(notification.entityId)}`;
	if (entityType === TENANT_ENTITY_TYPE) return NOTIFICATIONS_ROUTE;
	return null;
};

export const isNotificationUnread = (notification: NotificationFragment): boolean => !notification.readAt;
